import { Button, RadioBoxInput } from "@/components/ui";
import { AppModal } from "@/components/ui/AppModal/AppModal";
import type { VehicleRelatedMatterPlacement } from "@/lib/data/requests/matters/matter-vehicle";
import pluralize from "pluralize";
import { useEffect, useState } from "react";

interface CopyDestination {
  clientId: null | string;
  clientName: null | string;
  id: string;
  title: string;
}

const placementOptions = [
  { label: "Driver", value: "driver" },
  { label: "Passenger", value: "passenger" },
  { label: "Neither", value: "other_party" },
] as const;

type Placement = VehicleRelatedMatterPlacement["placement"];

export function VehicleCopyModeModal({
  destinations,
  open,
  onOpenChange,
  onSubmit,
  loading = false,
}: {
  destinations: CopyDestination[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (placements: VehicleRelatedMatterPlacement[]) => void;
  loading?: boolean;
}) {
  const [placementsByMatterId, setPlacementsByMatterId] = useState<
    Record<string, Placement>
  >({});

  useEffect(() => {
    if (open) {
      setPlacementsByMatterId(
        Object.fromEntries(
          destinations.map(({ id }) => [id, "passenger" as const]),
        ),
      );
    }
  }, [destinations, open]);

  const setPlacement = (matterId: string, placement: Placement) => {
    setPlacementsByMatterId((current) => {
      const next = { ...current };
      if (placement === "driver") {
        for (const destination of destinations) {
          if (next[destination.id] === "driver") {
            next[destination.id] = "passenger";
          }
        }
      }
      next[matterId] = placement;
      return next;
    });
  };

  return (
    <AppModal
      open={open}
      onOpenChange={onOpenChange}
      title={`What role do you want to set the related ${pluralize("client", destinations.length)} to on this vehicle?`}
      description="Choose one role for each destination client."
      size="xl"
      overlayClassName="z-[108] bg-black/50 backdrop-blur-md"
      className="z-[109]"
      loading={loading}
      footer={
        <div className="flex w-full justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            disabled={loading}
            onClick={() => {
              onOpenChange(false);
            }}
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={loading || destinations.length === 0}
            onClick={() => {
              onSubmit(
                destinations.map(({ id }) => ({
                  matterId: id,
                  placement: placementsByMatterId[id] ?? "passenger",
                })),
              );
            }}
          >
            Save
          </Button>
        </div>
      }
    >
      <div className="divide-border divide-y rounded-lg border">
        {destinations.map((destination) => (
          <div key={destination.id} className="space-y-3 px-4 py-3">
            <p className="min-w-0 text-sm">
              <span className="font-medium">{destination.title}</span>
              <span className="text-muted-foreground">
                {" — "}
                {destination.clientName || "Client unavailable"}
              </span>
            </p>
            <RadioBoxInput
              ariaLabel={`Vehicle role for ${destination.title}`}
              name={`vehicle-copy-${destination.id}`}
              options={placementOptions}
              value={placementsByMatterId[destination.id] ?? "passenger"}
              disabled={loading}
              onValueChange={(placement) => {
                setPlacement(destination.id, placement);
              }}
            />
          </div>
        ))}
      </div>
    </AppModal>
  );
}
